import { Icon } from '@iconify/react'
import { format } from 'date-fns'

import {
  ProgressBar,
  useGlobalState,
  useGetBulkDownloadStatus,
} from '@pepper/ui'

import { useSelectedCompanyId } from 'utils/functions/misc'
import { ReactComponent as FileSearchIcon } from 'assets/images/u:file-search-alt.svg'

import styles from './BulkDownloadStatus.module.css'

function BulkDownloadStatus(): JSX.Element | null {
  const { bulkDownloadJob, setBulkDownloadJob } = useGlobalState()
  const companyId = useSelectedCompanyId()

  const bulkDownloadStatus = useGetBulkDownloadStatus({
    data: { jobId: bulkDownloadJob?.id, count: bulkDownloadJob?.count },
  })

  const handleCloseSaveDocs = () => setBulkDownloadJob(null)

  const getDownloadFileName = () => {
    const projectName = bulkDownloadJob?.name ?? 'Project'
    const date = format(new Date(), 'dd-MM-yyyy')
    const filename = `${projectName}_${date}_PepperDocs.zip`
    return filename
  }

  const downloadFile = async (
    fileUrl: string,
    filename: string | undefined
  ) => {
    let _url = fileUrl

    // convert download link into blob in-order to change
    if (filename?.endsWith('.zip')) {
      const blob = await fetch(fileUrl).then(response => response.blob())
      _url = URL.createObjectURL(blob)
    }

    const anchorTag = document.createElement('a')
    anchorTag.style.display = 'none'
    anchorTag.href = _url
    anchorTag.download = getDownloadFileName()
    document.body.appendChild(anchorTag)
    anchorTag.click()
    URL.revokeObjectURL(_url)
    anchorTag.remove()
  }

  if (
    bulkDownloadJob === null ||
    bulkDownloadJob?.companyId !== companyId ||
    !bulkDownloadStatus.data ||
    bulkDownloadStatus.isLoading
  ) {
    return null
  }

  return (
    <section className={styles.container}>
      {bulkDownloadStatus.data?.status === 'finished' && (
        <>
          <div className={styles.body}>
            <Icon
              className={styles.icon}
              icon="uil:file-check"
              width="40"
              height="40"
            />

            <div>
              <p className={styles.heading}>Docs are ready</p>
              <button
                className={styles.saveButton}
                onClick={() =>
                  downloadFile(
                    bulkDownloadStatus.data.files?.[0].url ?? '',
                    bulkDownloadStatus.data.files?.[0].filename
                  )
                }
              >
                Save Docs
              </button>
            </div>
          </div>

          <button className={styles.closeButton} onClick={handleCloseSaveDocs}>
            <Icon icon="fe:close" width="16" height="16" />
          </button>
        </>
      )}

      {['inprogress', 'reprocessing'].includes(
        bulkDownloadStatus.data?.status
      ) && (
        <>
          <div className={styles.body}>
            <FileSearchIcon className={styles.icon} width="40" height="40" />

            <div className={styles.content}>
              <p className={styles.heading}>Fetching Docs</p>
              <ProgressBar
                className={styles.progressBar}
                percent={bulkDownloadStatus.data.step_percent ?? 0}
                status="active"
              />
            </div>
          </div>
          <button className={styles.closeButton} onClick={handleCloseSaveDocs}>
            <Icon icon="fe:close" width="16" height="16" />
          </button>
        </>
      )}
    </section>
  )
}

export default BulkDownloadStatus
